import React from "react";

import LazyLoad from "react-lazyload";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import cx from "classnames";
import useBreakpoint from "utils/hooks/useBreakpoint";
import breakpoints from "utils/breakpoints";
import HeartIcon from "components/icons/Heart";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "tailwind.config.js";
import HeartFilled from "components/icons/HeartFilled";
import quickStartLogo from "assets/quickStartLogo.svg";
import { getPriceDifferential } from "utils/getPriceDifferential";

const fullConfig = resolveConfig(tailwindConfig);

const boxShadow = css`
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.3);
`;

const Container = styled.div`
  max-width: 22rem;
  min-width: 20rem;
  box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.4s ease-out;

  ${({ isMobile }) =>
    isMobile
      ? boxShadow
      : `&:hover {
        ${boxShadow}
      }`}
`;

const Img = styled.img`
  transition: all 0.4s ease-out;
`;

const IconContainer = styled.div`
  min-width: 2.25rem;
  height: 2.25rem;
  @media ${breakpoints.md} {
    ${Container}:hover & {
      color: white;
      background-color: ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
        showNewSchemaColor
          ? `${newSchemaColorHexaCode}`
          : `${fullConfig.theme.colors["mattamy-blue"]}`};
    }
  }
`;

const Content = styled.div`
  transition: all 0.4s ease-out;
  ${({ isQMI }) => (isQMI ? `height: 30rem;` : `height: 29rem;`)}
  ${({ isMobile }) => isMobile && `height: 100%;`}
  ${({ isMobile }) =>
    isMobile &&
    `
    max-height: 100%;
    height: auto;
  `}
`;

const ImageContainer = styled.div`
  height: 13rem;
`;

const StyledButton = styled.div`
  height: 3.5rem;
  background-color: ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
    showNewSchemaColor
      ? `${fullConfig.theme.colors["white"]}`
      : `${fullConfig.theme.colors["action-blue"]}`};
  @media ${breakpoints.md} {
    ${Container}:hover & {
      background-color: ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
        showNewSchemaColor
          ? `${newSchemaColorHexaCode}`
          : `${fullConfig.theme.colors["mattamy-blue"]}`};
      color: ${({ showNewSchemaColor, newSchemaTextColorHexaCode }) =>
        showNewSchemaColor
          ? `${newSchemaTextColorHexaCode}`
          : `${fullConfig.theme.colors["white"]}`};
    }
    }
  }
`;

const AttributeContainer = styled.p`
  min-width: 6.625rem;
`;

const AvailableFlag = styled.div`
  border-radius: 0.813rem;
  width: fit-content;
  @media ${breakpoints.md} {
    ${Container}:hover & {
      color: white;
      background-color: ${({ showNewSchemaColor, newSchemaColorHexaCode }) =>
        showNewSchemaColor
          ? `${newSchemaColorHexaCode}`
          : `${fullConfig.theme.colors["mattamy-blue-light"]}`};
    }
  }
`;
const QMIFlagMessageContainer = styled.div`
  border-top-right-radius: 0.813rem;
  border-bottom-right-radius: 0.813rem;
  width: fit-content;
  border-top-width: 0.125rem;
  border-bottom-width: 0.125rem;
  border-right-width: 0.125rem;
`;

const QuickStartLogoFlag = styled.div`
  border-radius: 0.813rem;
  width: fit-content;
`;

const isStudio = (bedsLabel) => {
  if (bedsLabel && bedsLabel === "0") return true;
  return false;
};

const isZeroValue = (label) => {
  if (label) {
    const labelNumber = label.split(" ")[0];
    if (labelNumber && labelNumber === "0") return true;
    return false;
  }
  return true;
};

const getDefaultContentTextValue = (attributes) => {
  for (let i = 0; i < attributes.length; i++) {
    if (!isZeroValue(attributes[i].label) && attributes[i].icon !== "stories")
      return false;
  }
  return true;
};

const QMICard = ({ card = {}, ...rest }) => {
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const country = rest.country;
  const newSchemaColorHexaCode = rest.newSchemaColorHexaCode;
  const newSchemaTextColorHexaCode = rest.newSchemaTextColorHexaCode;
  const showNewSchemaColor = rest.showNewSchemaColor;
  const favoritesList = rest.favoritesList;

  const beds = card.attributes && card.attributes.find((e) => e.icon === "bed");
  const baths =
    card.attributes && card.attributes.find((e) => e.icon === "bath");
  const halfBaths =
    card.attributes && card.attributes.find((e) => e.icon === "half-baths");
  const sqft =
    card.attributes && card.attributes.find((e) => e.icon === "ruler");
  const garage =
    card.attributes && card.attributes.find((e) => e.icon === "car");
  const rvGarage = card.rvGarage;
  const availableDateMessage = card.availableDateMessage;
  const modelHome = card.modelHome;
  const type = card.type;
  const id = card.id;
  const homeType = card.homeType || "";
  const planName = card.planName || "";
  const maxTittleLength = isMobile ? 23 : 25;
  const usContentText = card.usContentText || "Floorplan details coming soon";
  const caContentText = card.caContentText || "Floorplan details coming soon";
  const preSellingHome = card.preSellingHome;
  const countryQMI = card.countryQMI;
  const isFavorite = favoritesList.includes(id);
  const messageFlag = card.messageFlag;
  //console.log(card.title, { messageFlag, modelHome });
  const priceDifferentialText = rest.priceDifferentialText;

  const cleanLabel = (label) => {
    const labelValues = label.split(" ");
    if (labelValues && labelValues.length > 0) {
      const ranges = labelValues[0].split("-");
      if (ranges.length > 1) {
        let newLabel = "";
        if (ranges[0] !== "0") {
          newLabel = newLabel + ranges[0];
        }
        if (ranges[0] !== "0" && ranges[1] !== "0") {
          newLabel = newLabel + "-";
        }
        if (ranges[1] !== "0" && ranges[1] !== "0") {
          newLabel = newLabel + ranges[1];
        }
        if (newLabel) {
          newLabel = newLabel + " " + labelValues[1];
          if (labelValues[2]) newLabel = newLabel + " " + labelValues[2];
        }
        return newLabel;
      }
    }
    return label;
  };

  const getLabel = (label) => {
    const newLabel = cleanLabel(label);
    const labelValues = newLabel.split(" ");

    if (labelValues && labelValues.length > 0) {
      const ranges = labelValues[0].split("-");
      if (ranges.length > 1) {
        return ranges[1] > 1 ? newLabel + "s" : newLabel;
      }
      return ranges[0] > 1 ? newLabel + "s" : newLabel;
    }
    return newLabel;
  };

  return (
    <Container
      className={cx(
        "relative w-full flex flex-col justify-between overflow-hidden rounded transform",
        {
          "hover:-translate-y-2": !isMobile,
        }
      )}
      isMobile={isMobile}
      onKeyUp={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.keyCode === 13) {
          window.location.href = card.url;
        }
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        window.location.href = card.url;
      }}
    >
      <Content
        className="md:cursor-pointer z-20 bg-white"
        isMobile={isMobile}
        isQMI={type === "QMI" && (planName !== "" || homeType !== "")}
      >
        <LazyLoad height={208}>
          <ImageContainer>
            {messageFlag && (
              <p className="absolute top-0 left-0 mt-10">
                <QMIFlagMessageContainer
                  className={cx(
                    "flex items-center justify-center px-6 py-1 mt-10-mattamy md:mt-1 text-promo-dark-blue bg-aqua border-promo-dark-blue rounded-r"
                  )}
                >
                  <p className="text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1">
                    {messageFlag}
                  </p>
                </QMIFlagMessageContainer>
              </p>
            )}
            {type === "QMI" &&
              (preSellingHome && countryQMI === "United States" ? (
                <p
                  className={cx("absolute top-0 left-0 ml-3 mt-10-mattamy", {})}
                  aria-hidden="true"
                >
                  <QuickStartLogoFlag className="flex items-center justify-center h-6 px-3 mt-10-mattamy mr-3 md:mt-0 bg-white ">
                    <img
                      className="object-cover h-full rounded"
                      src={quickStartLogo}
                      alt="quickStartLogo..."
                    />
                  </QuickStartLogoFlag>
                </p>
              ) : (
                availableDateMessage && (
                  <span
                    href={card.url}
                    className="absolute top-0 left-0 mt-10-mattamy"
                  >
                    <AvailableFlag
                      className={cx(
                        "ml-3 flex items-center justify-center ",
                        "px-6 py-1 mt-10-mattamy mr-3 md:mt-0 bg-white",
                        "hover:md:text-white",
                        {
                          "text-mattamy-blue-light hover:md:bg-mattamy-blue-light": !showNewSchemaColor,
                          "text-black hover:md:bg-black": showNewSchemaColor,
                        }
                      )}
                      showNewSchemaColor={showNewSchemaColor}
                      newSchemaColorHexaCode={newSchemaColorHexaCode}
                    >
                      <p className="text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1">
                        {availableDateMessage}
                      </p>
                    </AvailableFlag>
                  </span>
                )
              ))}
            {type === "Plan" && modelHome && (
              <span className="absolute top-0 left-0 mt-10-mattamy ml-3">
                <AvailableFlag
                  className={cx(
                    "flex items-center justify-center ",
                    "px-6 py-1 mt-10-mattamy mr-3 md:mt-0 bg-white",
                    "hover:md:text-white",
                    {
                      "text-mattamy-blue-light hover:md:bg-mattamy-blue-light": !showNewSchemaColor,
                      "text-black hover:md:bg-black": showNewSchemaColor,
                    }
                  )}
                >
                  <p className="text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1">
                    {modelHome}
                  </p>
                </AvailableFlag>
              </span>
            )}
            <StyledButton
              className={cx(
                "flex items-center justify-center w-full absolute bottom-0 left-0",
                {
                  "rounded-b border border-black text-black": showNewSchemaColor,
                  "text-white": !showNewSchemaColor,
                }
              )}
              showNewSchemaColor={showNewSchemaColor}
              newSchemaColorHexaCode={newSchemaColorHexaCode}
              newSchemaTextColorHexaCode={newSchemaTextColorHexaCode}
            >
              <p
                 tabIndex={-1}
                aria-label={
                  (card.price.price && card.price.price !== "$0"
                    ? `${card.price.normalText} ${card.price.price}`
                    : card.price.noPriceText) +
                  (card.price.displayPreviousPrice
                    ? " Previous price: " + card.price.previousPriceQMI
                    : "") +
                  " View "
                }
                className="md:cursor-pointer text-2xl font-trade-gothic-20 leading-6 mt-2"
              >
                {card.price.price && card.price.price !== "$0"
                  ? `${card.price.normalText} ${card.price.price}`
                  : card.price.noPriceText}
                {card.price.displayPreviousPrice && (
                  <span role="presentation" className="sr-only">
                    &nbsp;Was&nbsp;
                  </span>
                )}
                {card.price.displayPreviousPrice && (
                  <span className="ml-4 line-through text-aqua">
                    {card.price.previousPriceQMI}
                  </span>
                )}
                {card.price.displayPreviousPrice && (
                  <>
                    <span className="text-xs leading-2 text-aqua font-graphie ml-4">
                      &nbsp;{priceDifferentialText}&nbsp;
                    </span>
                    <span className="text-normal md:text-lg leading-5 font-trade-gothic-20 text-aqua ml-1">
                      {getPriceDifferential(
                        card.price.previousPriceQMI,
                        card.price.price
                      )}
                    </span>
                  </>
                )}
              </p>
            </StyledButton>
            <Img
              className="object-cover h-full min-w-full"
              src={card.image.src}
              alt={card.image.alt}
            />

            <IconContainer
              className={cx(
                "flex items-center absolute top-0 right-0 justify-center rounded-full bg-white mt-10-mattamy mr-3 hover:md:text-white md:cursor-pointer",
                {
                  "hover:-translate-y-2": !isMobile,
                  "text-action-blue hover:md:bg-mattamy-blue": !showNewSchemaColor,
                  "text-black hover:md:bg-black": showNewSchemaColor,
                }
              )}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                rest.handleFavoriteClick(id);
              }}
              role="button"
              as="button"
              aria-label={
                isFavorite ? "Mark as unfavorite" : "Mark as favorite"
              }
              tabIndex={0}
              aria-pressed={isFavorite ? "true" : "false"}
              // aria-hidden={true}
              showNewSchemaColor={showNewSchemaColor}
              newSchemaColorHexaCode={newSchemaColorHexaCode}
            >
              {isFavorite ? (
                <HeartFilled className="h-5 w-5" />
              ) : (
                <HeartIcon className="h-5 w-5" />
              )}
            </IconContainer>
          </ImageContainer>
        </LazyLoad>
        <a
          // tabIndex={-1}
          href={card.url}
          className="mx-5 my-4 flex flex-col w-auto"
          aria-label={
            card.community +
            " " +
            (type === "QMI"
              ? planName
                ? `${planName} Floorplan | ${homeType}`
                : homeType
              : homeType) +
            " " +
            card.title +
            " " +
            cleanLabel(sqft.label + " " + sqft.attribute) +
            " " +
            getLabel(beds.label + " " + beds.attribute) +
            " " +
            getLabel(baths.label + " " + baths.attribute) +
            " " +
            getLabel(halfBaths.label + " " + halfBaths.attribute) +
            " " +
            cleanLabel(garage.label + " " + garage.attribute) +
            " "
          }
        >
          {type === "Plan" || type === "QMI" ? (
            <span
              className={cx("font-graphie text-sm", {
                "text-action-blue": !showNewSchemaColor,
                "text-black": showNewSchemaColor,
              })}
            >
              {type === "QMI"
                ? planName
                  ? `${planName} Floorplan | ${homeType}`
                  : homeType
                : homeType}
            </span>
          ) : (
            ""
          )}
          <span
            className={cx(
              "font-trade-gothic-20 text-3xl uppercase whitespace-normal",
              {
                "text-mattamy-blue": !showNewSchemaColor,
                "text-black": showNewSchemaColor,
              }
            )}
          >
            {card.title.length > maxTittleLength
              ? card.title.substring(0, maxTittleLength - 1) + "..."
              : card.title}
          </span>
          <div
            className={cx("flex flex-col w-full h-32 font-graphie", {
              "text-mattamy-blue": !showNewSchemaColor,
              "text-black": showNewSchemaColor,
            })}
          >
            {getDefaultContentTextValue(card.attributes) && type === "Plan" ? (
              <p className="text-left text-base leading-5 pt-1">
                {country === "USA" ? usContentText : caContentText}
              </p>
            ) : (
              <>
                <div className="flex justify-left w-full pt-1">
                  {sqft && sqft.label !== "0" && (
                    <div className="flex flex-col justify-left">
                      <p className="text-left text-base leading-5 mb-2">
                        {cleanLabel(sqft.label + " " + sqft.attribute)}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex justify-left w-full pt-3">
                  {beds && isStudio(beds.label) && (
                    <AttributeContainer className="text-left text-base leading-5 mb-2">
                      {"Studio"}
                    </AttributeContainer>
                  )}
                  {beds && !isStudio(beds.label) && (
                    <AttributeContainer className="text-left text-base leading-5 mb-2">
                      {getLabel(beds.label + " " + beds.attribute)}
                    </AttributeContainer>
                  )}
                  {baths && baths.label !== "0" && (
                    <>
                      {((beds && isStudio(beds.label)) ||
                        (beds && !isStudio(beds.label))) && (
                        <span
                          className={cx(
                            "mx-3 font-graphie text-base leading-5 mb-2 ",
                            {
                              "text-aqua": !showNewSchemaColor,
                            }
                          )}
                        >
                          |
                        </span>
                      )}
                      <p className="text-left text-base leading-5 mb-2">
                        {getLabel(baths.label + " " + baths.attribute)}
                      </p>
                    </>
                  )}
                </div>
                <div className="flex justify-left w-full pt-2">
                  {halfBaths && halfBaths.label !== "0" && (
                    <AttributeContainer className="text-left text-base leading-5 mb-2">
                      {getLabel(halfBaths.label + " " + halfBaths.attribute)}
                    </AttributeContainer>
                  )}
                  {garage && garage.label !== "0" && (
                    <>
                      {halfBaths && halfBaths.label !== "0" && (
                        <span
                          className={cx(
                            "mx-3 font-graphie text-base leading-5 mb-2 ",
                            {
                              "text-aqua": !showNewSchemaColor,
                            }
                          )}
                        >
                          |
                        </span>
                      )}
                      <div className="flex justify-center">
                        <p className="text-center text-base leading-5">
                          {cleanLabel(
                            rvGarage
                              ? garage.label + " Car + RV Garage"
                              : garage.label + " " + garage.attribute
                          )}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </a>
      </Content>
    </Container>
  );
};

QMICard.propTypes = {
  card: PropTypes.object,
};

export default QMICard;
